import { Modal } from "@mui/material";
import { FC, useRef } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { ErrorDisplayUnsavedItems } from "@ryde/controls";
import { useConfirmDirtyField } from "@ryde/redux";

import { dashboardEditorAtom } from "../../dynamic-dashboard.atoms";
import { BACKDROP_SX } from "./dashboard-card-editor.constants";
import { CardEditorFields } from "./dashboard-card-editor.fields";
import { EditorPanelStyled } from "./dashboard-card-editor.styled";

/**
 * Card edit panel (PRD "Bar chart - Edit panel" / Feature 1). Opened from a
 * card's kebab or the header "New" button. Report + Group-by + Metric come from
 * the real reports engine (permission-filtered server-side); Save writes the
 * card into `dashboardCardsAtom`.
 *
 * Closing without saving goes through the same unsaved-changes prompt the edit
 * forms use, so a stray backdrop click can't silently discard the draft.
 */
export const DynamicDashboardCardEditor: FC = () => {
  const [editor, setEditor] = useRecoilState(dashboardEditorAtom);
  const isDirtyRef = useRef(false);

  const performClose = useEvent((): void => {
    isDirtyRef.current = false;
    setEditor({ isOpen: false, cardId: null });
  });

  const onDirtyChange = useEvent((isDirty: boolean): void => {
    isDirtyRef.current = isDirty;
  });

  const { applyIfConfirmed } = useConfirmDirtyField({
    customContent: <ErrorDisplayUnsavedItems />,
  });

  const requestClose = applyIfConfirmed({
    isDirty: () => isDirtyRef.current,
  })(performClose);

  if (!editor.isOpen) {
    return null;
  }

  return (
    <Modal
      open
      onClose={requestClose}
      slotProps={{ backdrop: { sx: BACKDROP_SX } }}
    >
      <EditorPanelStyled tabIndex={-1}>
        <div className="editor-header">
          <button
            type="button"
            className="close-btn"
            aria-label="Close"
            onClick={requestClose}
          >
            <SVG_MUI.XMarkSVG />
          </button>
        </div>
        <CardEditorFields
          cardId={editor.cardId}
          onClose={performClose}
          onDirtyChange={onDirtyChange}
        />
      </EditorPanelStyled>
    </Modal>
  );
};
